import React, { useEffect, useMemo, useState } from 'react';
import { Trophy, CalendarCheck, TrendingUp, Flag } from 'lucide-react';
import { listFootballPracticeTimes, listGameResults } from '../lib/api';

const DIVISIONS = ['7U', '9U', '11U', '13U'];
const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
const nextGameDate = (schedule) => schedule.filter((g) => g.sessionType === 'game' && !g.cancelled && !g.result && g.date >= today()).map((g) => g.date).sort()[0] || null;
const roundLabel = (game) => (/championship/i.test(game?.notes || '') ? 'Division 2 Championship' : /playoff/i.test(game?.notes || '') ? 'Playoff Round 1' : 'Next game');

const longDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
const clock = (iso, hhmm) => new Date(`${iso}T${hhmm}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export const buildPlayoffRows = (results, schedule) => {
  const KEY_DATE = nextGameDate(schedule);
  return DIVISIONS.map((division) => {
    const played = results.filter((g) => g.division === division && g.result);
    const wins = played.filter((g) => g.result.outcome === 'win').length;
    const losses = played.filter((g) => g.result.outcome === 'loss').length;
    const ties = played.length - wins - losses;
    const pf = played.reduce((s, g) => s + g.result.patriotsScore, 0);
    const pa = played.reduce((s, g) => s + g.result.opponentScore, 0);
    const upcoming = schedule
      .filter((g) => g.division === division && g.sessionType === 'game' && !g.cancelled && !g.result && g.date >= KEY_DATE)
      .sort((a, b) => a.date.localeCompare(b.date));
    const keyGame = upcoming.find((g) => g.date === KEY_DATE) || null;
        const fmt = (w, l) => `${w}-${l}${ties ? `-${ties}` : ''}`;
    return { division, keyDate: KEY_DATE, played: played.length, wins, losses, ties, pf, pa, record: fmt(wins, losses), ifWin: fmt(wins + 1, losses), keyGame };
  });
};

export const PlayoffTracker = ({ dark = false }) => {
  const [results, setResults] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    Promise.all([listGameResults().catch(() => []), listFootballPracticeTimes().catch(() => [])])
      .then(([r, s]) => { setResults(r); setSchedule(s); setStatus('ready'); })
      .catch(() => setStatus('error'));
  }, []);
  const rows = useMemo(() => buildPlayoffRows(results, schedule), [results, schedule]);
  if (status !== 'ready' || !rows[0].keyDate) return null;
  const keyDate = rows[0].keyDate;
  const anyKey = rows.find((r) => r.keyGame)?.keyGame;
  const shell = dark ? 'bg-[#0a1f44] text-white border-t-4 border-[#f4b400]' : 'bg-white text-[#0a1f44] border border-[#0a1f44]/15 border-t-4 border-t-[#c8102e]';
  const muted = dark ? 'text-white/60' : 'text-[#0a1f44]/55';
  const cell = dark ? 'bg-white/5' : 'bg-[#f5f6f8]';
  return (
    <section className={`${shell} p-5 sm:p-6 mt-6`} aria-labelledby="playoff-tracker-heading" data-testid="playoff-tracker">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-heading uppercase tracking-[0.3em] text-[10px] text-[#f4b400]"><Trophy className="w-4 h-4" aria-hidden="true" /> Road to the Playoffs</div>
          <h2 id="playoff-tracker-heading" className="font-display text-2xl sm:text-3xl mt-1">{roundLabel(anyKey)} · {longDate(keyDate)} — where each team stands</h2>
        </div>
        <div className={`font-heading uppercase tracking-widest text-[10px] ${muted}`}>Records from results posted on this site</div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((r) => (
          <article key={r.division} className={`${cell} p-4 flex flex-col gap-3`} data-testid={`playoff-row-${r.division}`}>
            <div className="flex items-baseline justify-between">
              <div className="font-display text-3xl text-[#c8102e]">{r.division}</div>
              <div className="text-right">
                <div className={`font-heading uppercase tracking-widest text-[9px] ${muted}`}>Record</div>
                <div className="font-display text-3xl tabular-nums leading-none" data-testid={`playoff-record-${r.division}`}>{r.played ? r.record : '—'}</div>
              </div>
            </div>
            <div className={`flex items-center gap-1.5 text-xs ${muted}`} data-testid={`playoff-points-${r.division}`}><TrendingUp className="w-3.5 h-3.5 text-[#f4b400]" aria-hidden="true" />{r.played ? `${r.pf} for · ${r.pa} against · ${r.played} game${r.played === 1 ? '' : 's'} posted` : 'No results posted yet'}</div>
            {r.keyGame ? (
              <div className="text-sm" data-testid={`playoff-keygame-${r.division}`}>
                <div className="flex items-center gap-1.5 font-heading uppercase tracking-widest text-[9px] text-[#f4b400]"><CalendarCheck className="w-3.5 h-3.5" aria-hidden="true" /> {longDate(r.keyGame.date)} · {clock(r.keyGame.date, r.keyGame.endTime || r.keyGame.startTime)}</div>
                <div className="font-semibold mt-1">vs {r.keyGame.opponent}{/home team/i.test(r.keyGame.notes || '') ? ' (Home)' : /away/i.test(r.keyGame.notes || '') ? ' (Away)' : ''}</div>
                <div className={`mt-1 text-xs ${muted}`}>Win → <span className="text-emerald-500 font-semibold">{/championship/i.test(r.keyGame.notes || '') ? 'Division 2 champions!' : 'advance to the next round'}</span>{r.played > 0 && <> · record would be <span className="font-semibold">{r.ifWin}</span></>}</div>
                <div className={`mt-1 flex items-center gap-1.5 text-xs ${muted}`} data-testid={`playoff-stakes-${r.division}`}><Flag className="w-3.5 h-3.5 text-[#c8102e]" aria-hidden="true" />{roundLabel(r.keyGame)} · {/home team/i.test(r.keyGame.notes || '') ? 'Patriots are the home team' : /away/i.test(r.keyGame.notes || '') ? 'Patriots are the away team' : ''} · {(r.keyGame.location || '').split(',')[0]}</div>
              </div>
            ) : (
              <div className={`text-sm ${muted}`} data-testid={`playoff-keygame-${r.division}`}>No game on {longDate(keyDate)} — season record shown.</div>
            )}
          </article>
        ))}
      </div>
      <p className={`mt-4 text-xs ${muted}`}>Records come from results staff post on this site after each game, so this card updates automatically — playoff matchups follow the league bracket.</p>
    </section>
  );
};
